import Page from "./Page";
import { Box, Typography } from "@mui/material";

const PrivacyPolicyPage = () => {
  return (
    <Page pageKey="privacy-policy" className="privacy-policy-page">
      <Box
        sx={{ maxWidth: 900, mx: "auto", p: 3 }}
        className="privacy-policy-content"
      >
        <Typography variant="h1" gutterBottom>
          Privacy Policy for Mindstack
        </Typography>

        <Typography variant="subtitle1" gutterBottom>
          Last updated: 3rd February 2026
        </Typography>

        <Typography variant="p1">
          Mindstack ("we", "our", or "us") respects your privacy and is
          committed to protecting your personal data. This Privacy Policy
          explains how we collect, use, store, and protect your information when
          you use the Mindstack website and mobile application (together, the
          "Service"). Mindstack is operated from the United Kingdom and complies
          with the UK General Data Protection Regulation (UK GDPR) and the Data
          Protection Act 2018.
        </Typography>

        <Typography variant="h2" gutterBottom>
          1. Information We Collect
        </Typography>

        <Typography variant="p1">
          We collect only the information necessary to provide and improve
          Mindstack.
        </Typography>

        <Typography variant="h3" gutterBottom>
          a. Personal Information You Provide
        </Typography>
        <ul style={{ marginBottom: 32 }}>
          <li>
            <Typography variant="p1">Name or username</Typography>
          </li>
          <li>
            <Typography variant="p1">Email address</Typography>
          </li>
          <li>
            <Typography variant="p1">
              To-do items, task metadata, and related notes you choose to enter
            </Typography>
          </li>
        </ul>

        <Typography variant="h3" gutterBottom>
          b. Usage and Technical Information
        </Typography>
        <Typography variant="p1" gutterBottom>
          We may automatically collect:
        </Typography>
        <ul style={{ marginBottom: 32 }}>
          <li>
            <Typography variant="p1">
              Device type, operating system, and browser
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              App or website usage data (e.g. features used, timestamps)
            </Typography>
          </li>
        </ul>

        <Typography variant="h3" gutterBottom>
          c. Cookies and Similar Technologies
        </Typography>
        <Typography variant="p1" sx={{ mb: 4 }}>
          We use cookies and similar technologies to keep you logged in,
          understand how the Service is used, and improve performance and
          reliability. You can manage cookies through your browser or device
          settings.
        </Typography>

        <Typography variant="h2" gutterBottom>
          2. How We Use Your Information
        </Typography>
        <Typography variant="p1" paragraph>
          We use your information to:
        </Typography>
        <ul style={{ marginBottom: 32 }}>
          <li>
            <Typography variant="p1">
              Provide and operate the Mindstack Service
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Organise and display your tasks and to-do lists
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Maintain your account and authenticate users
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Improve functionality and user experience
            </Typography>
          </li>
          <li>
            <Typography variant="p1">Respond to support requests</Typography>
          </li>
          <li>
            <Typography variant="p1">
              Ensure security and prevent misuse
            </Typography>
          </li>
          <li>
            <Typography variant="p1">Comply with legal obligations</Typography>
          </li>
        </ul>

        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          We do not use your task content for advertising purposes.
        </Typography>

        <Typography variant="h2" gutterBottom>
          3. Legal Basis for Processing (UK GDPR)
        </Typography>
        <Typography variant="p1" paragraph>
          Under UK GDPR, we process your data based on:
        </Typography>
        <ul style={{ marginBottom: 32 }}>
          <li>
            <Typography variant="p1">
              Contractual necessity – to provide the Service you request
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Legitimate interests – to improve and secure Mindstack
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Consent – where required (e.g. optional communications)
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Legal obligation – where processing is required by law
            </Typography>
          </li>
        </ul>

        <Typography variant="h2" gutterBottom>
          4. How We Share Your Information
        </Typography>
        <Typography variant="p1" paragraph>
          We do not sell your personal data. We may share information only with:
        </Typography>
        <ul style={{ marginBottom: 32 }}>
          <li>
            <Typography variant="p1">
              Service providers (such as hosting, analytics, or authentication
              services) who process data on our behalf
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Legal or regulatory authorities if required by law
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Business successors in the event of a merger, acquisition, or sale
              of assets
            </Typography>
          </li>
        </ul>
        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          All third parties are required to protect your data and use it only
          for agreed purposes.
        </Typography>

        <Typography variant="h2" component="h2" gutterBottom>
          5. Data Storage and Retention
        </Typography>
        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          Your data is stored securely using reputable third-party
          infrastructure. We retain personal data only for as long as your
          account is active, necessary to provide the Service, or required by
          law. You may delete your account at any time, after which your data
          will be deleted or anonymised within a reasonable period.
        </Typography>

        <Typography variant="h2" component="h2" gutterBottom>
          6. Your Data Protection Rights (UK & EU)
        </Typography>
        <Typography variant="p1" paragraph>
          You have the right to:
        </Typography>
        <ul style={{ marginBottom: 32 }}>
          <li>
            <Typography variant="p1">Access your personal data</Typography>
          </li>
          <li>
            <Typography variant="p1">
              Request correction of inaccurate data
            </Typography>
          </li>
          <li>
            <Typography variant="p1">Request deletion of your data</Typography>
          </li>
          <li>
            <Typography variant="p1">
              Object to or restrict processing
            </Typography>
          </li>
          <li>
            <Typography variant="p1">Request data portability</Typography>
          </li>
          <li>
            <Typography variant="p1">Withdraw consent at any time</Typography>
          </li>
        </ul>
        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          To exercise your rights, contact us using the details below.
        </Typography>

        <Typography variant="h2" component="h2" gutterBottom>
          7. Data Security
        </Typography>
        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          We use appropriate technical and organisational measures to protect
          your personal data using relevant access controls. However, task
          contents are not currently encrypted, and no system is completely
          secure. We will work to encrypt all tasks at-rest when we have
          capacity, but until that time we cannot guarantee absolute security so
          please do not store sensitive information in your tasks.
        </Typography>

        <Typography variant="h2" component="h2" gutterBottom>
          8. Children’s Privacy
        </Typography>
        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          Mindstack is not intended for use by children under the age of 13. We
          do not knowingly collect personal data from children.
        </Typography>

        <Typography variant="h2" component="h2" gutterBottom>
          9. Third-Party Services
        </Typography>
        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          Mindstack may integrate with or link to third-party services. We are
          not responsible for the privacy practices of those third parties.
        </Typography>

        <Typography variant="h2" component="h2" gutterBottom>
          10. Changes to This Privacy Policy
        </Typography>
        <Typography variant="p1" paragraph sx={{ mb: 4 }}>
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with a revised "Last updated" date.
        </Typography>

        <Typography variant="h2" component="h2" gutterBottom>
          11. Contact Information
        </Typography>
        <Typography variant="p1" paragraph>
          If you have any questions about this Privacy Policy or your data,
          please contact us:
        </Typography>
        <ul style={{ marginBottom: 32 }}>
          <li>
            <Typography variant="p1">
              Email:{" "}
              <Box component="span" sx={{ fontWeight: "bold" }}>
                mindstackapp@gmail.com
              </Box>
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Company Name:{" "}
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Mindstack Labs Ltd
              </Box>
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Country:{" "}
              <Box component="span" sx={{ fontWeight: "bold" }}>
                United Kingdom
              </Box>
            </Typography>
          </li>
          <li>
            <Typography variant="p1">
              Address:{" "}
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Bartle House, 9 Oxford Court, Manchester, England, M2 3WQ
              </Box>
            </Typography>
          </li>
        </ul>
      </Box>
    </Page>
  );
};

export default PrivacyPolicyPage;
